import { Add, Clear, Save } from '@mui/icons-material'
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  StyledContainerColumnLeft,
  StyledContainerRowBetween,
  StyledContainerRowEnd,
} from '../../../../styles/ContainerStyles'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  useAddUsersWorklogEntryMutation,
  useEditUsersWorklogEntryMutation,
} from '../../../../store/redux/reducers/worklogSlice'
import { Day, Project, WorkEntry } from '../../../../store/types'
import {
  ProjectAcronym,
  useGetAcronymsQuery,
  useGetProjectsQuery,
} from '../../../../store/redux/reducers/projectsSlice'
import { useEditMode, useNotification } from '../../../../store/redux/hooks'
import { getNumericTimeValue } from '../../../../store/utils/worklogUtils'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../../../store/redux/reducers/currentUserSlice'
import { SquareButton } from '../../../../styles/ButtonStyles'
import { getSidebarState } from '../../../../store/redux/reducers/sidebarSlice'
import DateFormat from '../../../../store/types/enums/DateFormat'
import { isEqual } from 'lodash'
import { LoadingButton } from '@mui/lab'
import CheckboxInput from '../../../Form/Inputs/CheckboxInput'
import TextInput from '../../../Form/Inputs/TextInput'
import AutocompleteInput from '../../../Form/Inputs/AutocompleteInput'
import {
  getDefaultWorklogEntryValues,
  WorklogEntryFormValues,
} from '../../../../store/types/form/WorklogEntryFormValues'
import { getCurrentDefaultProject, getCurrentRemoteWorkMode } from '../../../../store/redux/reducers/settingsSlice'
import { filterOptions } from '../../../../store/utils/filterProjectsAcronymsOptions'
import { createErrorMessage } from '../../../../store/utils'
import { useGetCurrentUserQuery } from '../../../../store/redux/reducers/usersSlice'
import { mediumDefaultPageSize } from '../../../../config/defaultPagination'

type AddEntryFormProps = {
  day: Day
  editMode: boolean
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
  editedEntry: WorkEntry | null
  setEditedEntry: React.Dispatch<React.SetStateAction<WorkEntry | null>>
  openFilterMenu: boolean
  projectIds: string[]
  setIsChangePresent: React.Dispatch<React.SetStateAction<boolean[]>>
  setEntryAddedFlag: React.Dispatch<React.SetStateAction<boolean>>
}

const AddEntryForm = ({
  day,
  editMode,
  setEditMode,
  editedEntry,
  setEditedEntry,
  openFilterMenu,
  projectIds,
  setIsChangePresent,
  setEntryAddedFlag,
}: AddEntryFormProps) => {
  const theme = useTheme()

  const remoteWorkModeFlag = useSelector(getCurrentRemoteWorkMode)
  const [favoriteProjects, setFavoriteProjects] = useState<Project[]>([])
  const defaultProject = useSelector(getCurrentDefaultProject)
  const { displayNotification } = useNotification()
  const { setIsEditMode } = useEditMode()
  const defaultValues = useMemo(() => {
    return getDefaultWorklogEntryValues(editedEntry, remoteWorkModeFlag, defaultProject)
  }, [editedEntry, remoteWorkModeFlag, defaultProject])
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitSuccessful, isDirty, dirtyFields },
    reset,
  } = useForm<WorklogEntryFormValues>({
    defaultValues: defaultValues,
  })

  const isImportedEntry = editMode && editedEntry?.fromImport === true
  const hoursModified = dirtyFields.hours === true
  const isNonStandardHours = isImportedEntry && editedEntry != null && editedEntry.hours % 0.5 !== 0
  const isImportedNonStandardDisabled = isNonStandardHours && !hoursModified

  const { data: currentUserWithSettings } = useGetCurrentUserQuery({})

  const settings = useMemo(() => currentUserWithSettings?.settings, [currentUserWithSettings])
  const workHoursSortByFavoriteFlag = settings?.workHoursSortByFavorite || false

  useEffect(() => {
    setIsChangePresent(prevState => prevState.map((item, index) => (index === day.id - 1 ? isDirty : item)))
  }, [isDirty, day.id, setIsChangePresent])

  useEffect(() => {
    if (editMode || editedEntry === null) {
      reset(defaultValues)
    }
  }, [editMode, editedEntry, reset, defaultValues])

  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const getAcronymProps = useCallback(() => {
    return {
      page: page,
      size: mediumDefaultPageSize,
      date: day.date.format(DateFormat.YearMonthDay),
      acronym: searchedAcronym,
      loggable: true,
      sortByWorkHours: !workHoursSortByFavoriteFlag,
      sortByFavorites: workHoursSortByFavoriteFlag,
    }
  }, [page, day.date, searchedAcronym, workHoursSortByFavoriteFlag])

  const [acronymProps, setAcronymProps] = useState(getAcronymProps())
  const { data: projectsResponse } = useGetAcronymsQuery(acronymProps)
  const [projects, setProjects] = useState<ProjectAcronym[]>([])

  const onSelectBottomScroll = () => {
    if (!projectsResponse?.last) {
      handleFetchMoreData()
    }
  }

  const handleFetchMoreData = () => {
    setPage(prev => prev + 1)
  }

  useEffect(() => {
    setAcronymProps(getAcronymProps())
  }, [getAcronymProps, page, searchedAcronym])

  useEffect(() => {
    setIsEditMode(true)
  })

  useEffect(() => {
    setPage(0)
  }, [searchedAcronym])

  useMemo(() => {
    if (projectsResponse) {
      setProjects(prevProjects => {
        const existingProjectIds = new Set(prevProjects.map(project => project.id))
        const newProjects = projectsResponse.content.filter(project => !existingProjectIds.has(project.id))
        return [...prevProjects, ...newProjects]
      })
    }
  }, [projectsResponse])

  const { data: favoriteProjectsResponse, isSuccess } = useGetProjectsQuery({
    page: 0,
    size: 100,
    favorite: true,
  })

  useEffect(() => {
    if (isSuccess) {
      setFavoriteProjects(favoriteProjectsResponse.content)
    }
  }, [favoriteProjectsResponse, isSuccess])

  const checkIfLastAddedProject = useCallback(
    (projectId: string) => {
      return projectIds.some(id => id === projectId)
    },
    [projectIds]
  )

  const checkIfFavoriteProject = useCallback(
    (projectId: string) => {
      return favoriteProjects.some(project => project.id === projectId)
    },
    [favoriteProjects]
  )

  const isActiveProject = (project: ProjectAcronym) => !['INACTIVE', 'FINISHED'].includes(project.projectStatus)

  const sortedProjects = useMemo(() => {
    const isRelevantAndActive = (project: ProjectAcronym) => {
      const isRelevant = workHoursSortByFavoriteFlag
        ? checkIfFavoriteProject(project.id)
        : checkIfLastAddedProject(project.id)
      return isRelevant && isActiveProject(project)
    }

    return [
      ...projects.filter(isRelevantAndActive),
      ...projects.filter(project => !isRelevantAndActive(project) && isActiveProject(project)),
    ]
  }, [checkIfFavoriteProject, checkIfLastAddedProject, projects, workHoursSortByFavoriteFlag])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedAcronym(value)
    }, 200)
  }, [])

  const [addEntry] = useAddUsersWorklogEntryMutation()
  const [editEntry] = useEditUsersWorklogEntryMutation()

  const currentUser = useSelector(getCurrentUser)
  const id = currentUser?.id === undefined ? '' : currentUser?.id

  const handleAddEntry: SubmitHandler<WorklogEntryFormValues> = useCallback(
    async data => {
      try {
        await addEntry({
          employeeId: id,
          entry: {
            date: day.date.format(DateFormat.YearMonthDay),
            hours: getNumericTimeValue(data.hours),
            description: data.description?.trim(),
            projectId: data.project?.value,
            remote: data.remote,
          },
        }).unwrap()
        reset(defaultValues)
        displayNotification({ message: t('page.worklog.alert.addEntrySuccess'), type: 'success' })
        setEntryAddedFlag(true)
      } catch (error) {
        let errorDescription = createErrorMessage(error)
        displayNotification({
          message: `${t('page.worklog.alert.addEntryError')}: ${errorDescription}`,
          type: 'error',
        })
        reset(data)
      }
    },
    [addEntry, id, day.date, reset, defaultValues, displayNotification, setEntryAddedFlag]
  )

  const handleCancelEdit = useCallback(() => {
    setEditMode(false)
    setIsEditMode(false)
    setEditedEntry(null)
    reset(defaultValues)
  }, [setEditMode, setIsEditMode, setEditedEntry, reset, defaultValues])

  const handleEditEntry: SubmitHandler<WorklogEntryFormValues> = useCallback(
    async data => {
      if (editedEntry === null) {
        displayNotification({ message: t('page.worklog.alert.idError'), type: 'error' })
        return
      }

      const isHoursUnchanged = isImportedEntry && !hoursModified

      if (
        isEqual(data.description, editedEntry.description) &&
        isEqual(data.remote, editedEntry.remote) &&
        isEqual(data.project?.value, editedEntry.project.id) &&
        (isHoursUnchanged || isEqual(getNumericTimeValue(data.hours), editedEntry.hours))
      ) {
        handleCancelEdit()
        return Promise.reject('Nothing changed')
      }
      try {
        const keepFromImport = isHoursUnchanged

        await editEntry({
          employeeId: id,
          entryId: editedEntry.id,
          entry: {
            date: day.date.format(DateFormat.YearMonthDay),
            ...(isHoursUnchanged ? {} : { hours: getNumericTimeValue(data.hours) }),
            description: data.description?.trim(),
            projectId: data.project?.value,
            remote: data.remote,
            version: editedEntry.version,
            fromImport: keepFromImport,
          },
        }).unwrap()
        handleCancelEdit()
        displayNotification({ message: t('page.worklog.alert.editEntrySuccess'), type: 'success' })
      } catch (error) {
        let errorDescription = createErrorMessage(error)
        displayNotification({
          message: `${t('page.worklog.alert.editEntryError')}: ${errorDescription}`,
          type: 'error',
        })
        reset(data)
      }
    },
    [editedEntry, displayNotification, handleCancelEdit, editEntry, id, day.date, reset, isImportedEntry, hoursModified]
  )

  const handleSave = useCallback(() => {
    handleSubmit(editMode ? handleEditEntry : handleAddEntry)().catch(error => error)
  }, [handleSubmit, editMode, handleEditEntry, handleAddEntry])

  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const activeElement = document.activeElement
        if (formRef.current?.contains(activeElement)) {
          e.preventDefault()
          handleSave()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleSave])

  const isSidebarOpen = useSelector(getSidebarState)

  const isUpMd = useMediaQuery(theme.breakpoints.up('md'))
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  const options = useMemo(() => {
    const mappedSortedProjects = sortedProjects.map(project => ({ label: project.acronym, value: project.id }))
    const isDefaultProjectInOptions =
      defaultProject && !sortedProjects.some(project => project.id === defaultProject.value)

    return isDefaultProjectInOptions ? [...mappedSortedProjects, defaultProject] : mappedSortedProjects
  }, [defaultProject, sortedProjects])

  return (
    <StyledContainerColumnLeft
      ref={formRef}
      sx={{
        flex: 1,
        gap: { xs: 1, lg: 2 },
        width: '100%',
      }}
    >
      <StyledContainerRowBetween>
        <Typography variant="h6" component="h3" sx={{ width: '100%' }}>
          {t(`page.worklog.form.${editMode ? 'edit' : 'new'}Entry`)}
        </Typography>
        <StyledContainerRowEnd sx={{ gap: 2, width: 'fit-content', px: 0 }}>
          {isUpMd || (isSm && !isSidebarOpen) ? (
            <Stack direction="row" gap={2}>
              {editMode && (
                <Button
                  startIcon={<Clear />}
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={isSubmitting || isSubmitSuccessful}
                  onClick={handleCancelEdit}
                >
                  {t(`page.worklog.form.cancel`)}
                </Button>
              )}
              <LoadingButton
                startIcon={editMode ? <Save /> : <Add />}
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={!isDirty || isSubmitting}
                loading={isSubmitting}
              >
                {t(`page.worklog.form.${editMode ? 'save' : 'add'}Entry`)}
              </LoadingButton>
            </Stack>
          ) : (
            <Stack direction="row" gap={2}>
              {editMode && (
                <SquareButton
                  color="secondary"
                  size="small"
                  disabled={isSubmitting || isSubmitSuccessful}
                  onClick={handleCancelEdit}
                >
                  <Clear />
                </SquareButton>
              )}
              <SquareButton color="primary" size="small" onClick={handleSave} disabled={isSubmitting}>
                {editMode ? <Save /> : <Add />}
              </SquareButton>
            </Stack>
          )}
        </StyledContainerRowEnd>
      </StyledContainerRowBetween>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row', lg: openFilterMenu ? 'column' : 'row', xl: 'row' },
          gap: 1,
        }}
      >
        <CheckboxInput name={'remote'} control={control} label={t('page.worklog.form.remote')} />
        <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <TextInput
            name={'hours'}
            control={control}
            required
            disabled={isImportedNonStandardDisabled}
            label={t('page.worklog.form.timeSpent')}
            placeholder={t('page.worklog.form.timeSpentPlaceholder')}
            rules={{
              required: t('page.worklog.form.timeSpentRequiredError'),
              validate: {
                pattern: value => {
                  if (isImportedEntry && !hoursModified) {
                    return true
                  }
                  const pattern = /^\s*(?:(([1-9]|1[0-9]|2[0-3])h)?\s*(30m)?)\s*$/
                  return pattern.test(String(value)) || t('page.worklog.form.timeSpentFormatError')
                },
              },
            }}
          />

          <AutocompleteInput
            options={options}
            onSelectBottomScroll={onSelectBottomScroll}
            name={'project'}
            control={control}
            required
            disabled={isImportedNonStandardDisabled}
            label={t('page.worklog.form.project')}
            rules={{ required: t('page.worklog.form.projectRequiredError') }}
            noOptionsText={t('page.worklog.form.projectsNotFound')}
            groupBy={
              workHoursSortByFavoriteFlag
                ? option => {
                    const isFavorite = checkIfFavoriteProject(option.value)
                    return isFavorite
                      ? t('page.worklog.form.yourFavoriteProjects')
                      : t('page.worklog.form.otherProjects')
                  }
                : option => {
                    const hasHoursInCurrentMonth = checkIfLastAddedProject(option.value)
                    return hasHoursInCurrentMonth
                      ? t('page.worklog.form.yourLastProjects')
                      : t('page.worklog.form.otherProjects')
                  }
            }
            listboxMaxHeight={250}
            handleInputChange={handleInputChange}
            aria-label={t('page.worklog.form.project')}
            filterOptions={(options, state) => filterOptions(options, state, sortedProjects)}
          />
        </Box>
      </Box>

      <TextInput
        name={'description'}
        control={control}
        label={t('page.worklog.form.largeDescription')}
        placeholder={t('page.worklog.form.descriptionPlaceholder')}
        maxLength={1000}
        rules={{
          maxLength: { value: 1000, message: t('page.worklog.form.descriptionLengthError') },
        }}
        multiline
        showCharCounter
        rows={11}
      />
    </StyledContainerColumnLeft>
  )
}

export default AddEntryForm
